// File: src/components/pages/admin/AdminDefaultTemplates.jsx
// Admin CRUD for TrackerDefaultTemplates — global default templates
// that ServiceProviders can pick from in BuildTracker.

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Plus, Trash2, Edit, Sparkles,
  AlertCircle, CheckCircle, PlusCircle, Tag
} from 'lucide-react';
import { trackerDefaultTemplateAPI } from '../../../services/api';
import '../../../styles/unified.css';

const TYPE_OPTIONS = [
  { value: '',          label: 'Select type…'  },
  { value: 'Scale',     label: 'Scale'          },
  { value: 'Checkbox',  label: 'Checkbox'       },
  { value: 'Radio',     label: 'Radio'          },
  { value: 'Text',      label: 'Text'           },
  { value: 'Pass/Fail', label: 'Pass / Fail'    },
  { value: 'Rating',    label: 'Rating'         },
];

const TYPE_COLOURS = {
  'Scale':     '#667eea',
  'Checkbox':  '#f59e0b',
  'Radio':     '#10b981',
  'Text':      '#6b7280',
  'Pass/Fail': '#ef4444',
  'Rating':    '#8b5cf6',
};

const EMPTY_ROW  = () => ({ item: '', type: '' });
const EMPTY_FORM = () => ({ heading: '', category: '', rows: [EMPTY_ROW()] });

const AdminDefaultTemplates = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [templates, setTemplates]   = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [isSaving,  setIsSaving]    = useState(false);
  const [error,     setError]       = useState(null);
  const [successMsg,setSuccessMsg]  = useState(null);
  const [showForm,  setShowForm]    = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [form,      setForm]        = useState(EMPTY_FORM());
  const [formErrors,setFormErrors]  = useState({});

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await trackerDefaultTemplateAPI.getAll(getAccessTokenSilently);
      if (res.success) setTemplates(res.data || []);
      else setError(res.message || 'Failed to load default templates');
    } catch (err) {
      setError('Failed to load default templates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // ── Form handlers ─────────────────────────────────────────────────────────

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleRowChange = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      rows: prev.rows.map((r, i) => i === index ? { ...r, [field]: value } : r)
    }));
    const key = `row_${index}_${field}`;
    if (formErrors[key]) setFormErrors(prev => ({ ...prev, [key]: '' }));
  };

  const handleAddRow = () =>
    setForm(prev => ({ ...prev, rows: [...prev.rows, EMPTY_ROW()] }));

  const handleRemoveRow = (index) => {
    setForm(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== index) }));
    setFormErrors(prev => {
      const next = { ...prev };
      delete next[`row_${index}_item`];
      delete next[`row_${index}_type`];
      return next;
    });
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validate = () => {
    const errs = {};
    if (!form.heading.trim()) errs.heading = 'Heading is required';
    form.rows.forEach((row, i) => {
      if (!row.item.trim()) errs[`row_${i}_item`] = 'Item is required';
      if (!row.type)        errs[`row_${i}_type`] = 'Type is required';
    });
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Open / close ──────────────────────────────────────────────────────────

  const handleNew = () => {
    setForm(EMPTY_FORM());
    setFormErrors({});
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (tpl) => {
    setForm({
      heading:  tpl.heading,
      category: tpl.category || '',
      rows:     (tpl.items || []).map(i => ({ item: i.item, type: i.type }))
    });
    setFormErrors({});
    setEditingId(tpl.defaultTemplateId);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM());
    setFormErrors({});
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSaving(true);
    setError(null);

    const payload = {
      heading:  form.heading.trim(),
      category: form.category.trim(),
      items:    form.rows.map(r => ({ item: r.item.trim(), type: r.type }))
    };

    try {
      const res = editingId
        ? await trackerDefaultTemplateAPI.update(editingId, payload, getAccessTokenSilently)
        : await trackerDefaultTemplateAPI.create(payload, getAccessTokenSilently);

      if (res.success) {
        showSuccess(editingId ? 'Default template updated' : 'Default template created');
        handleCancel();
        await loadTemplates();
      } else {
        setError(res.message || 'Failed to save default template');
      }
    } catch (err) {
      setError(err.message || 'Failed to save default template');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (defaultTemplateId) => {
    if (!window.confirm('Delete this default template? Instructors who have already copied it will not be affected.')) return;
    setError(null);
    try {
      const res = await trackerDefaultTemplateAPI.delete(defaultTemplateId, getAccessTokenSilently);
      if (res.success) { showSuccess('Default template deleted'); await loadTemplates(); }
      else setError(res.message || 'Failed to delete default template');
    } catch (err) {
      setError(err.message || 'Failed to delete default template');
    }
  };

  const formatDate = (d) => !d ? 'N/A' : new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });

  // ── Group by category for the list ────────────────────────────────────────
  const categories = [...new Set(templates.map(t => t.category || 'General'))].sort();

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Feedback */}
      {error && (
        <div className="error-banner" style={{ marginBottom: '1rem' }}>
          <AlertCircle size={18} /><span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="success-banner" style={{ marginBottom: '1rem' }}>
          <CheckCircle size={18} /><span>{successMsg}</span>
        </div>
      )}

      {/* Top bar */}
      <div className="search-bar-with-action" style={{ marginBottom: '1.25rem' }}>
        <div style={{ flex: 1 }} />
        {!showForm && (
          <button className="btn-new" onClick={handleNew}>
            <Plus size={20} />
            New Default Template
          </button>
        )}
      </div>

      {/* ── Form ── */}
      {showForm && (
        <div className="tracker-form-panel">
          <h3 className="tracker-form-title">
            {editingId ? 'Edit Default Template' : 'New Default Template'}
          </h3>

          {/* Heading */}
          <div className="tracker-form-field" style={{ marginBottom: '1rem' }}>
            <label className="tracker-form-label">Heading</label>
            <input
              type="text"
              value={form.heading}
              onChange={e => handleFieldChange('heading', e.target.value)}
              placeholder="e.g. Junctions"
              className={`tracker-form-input ${formErrors.heading ? 'input-error' : ''}`}
            />
            {formErrors.heading && <span className="field-error">{formErrors.heading}</span>}
          </div>

          {/* Category */}
          <div className="tracker-form-field" style={{ marginBottom: '1.5rem' }}>
            <label className="tracker-form-label">
              Category
              <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: '0.4rem' }}>
                (optional — used to group templates in the picker)
              </span>
            </label>
            <input
              type="text"
              value={form.category}
              onChange={e => handleFieldChange('category', e.target.value)}
              placeholder="e.g. Manoeuvres"
              className="tracker-form-input"
            />
          </div>

          {/* Column headers */}
          <div className="tracker-items-header">
            <span className="tracker-items-col-label">Item</span>
            <span className="tracker-items-col-label">Type</span>
          </div>

          {/* Item rows */}
          <div className="tracker-items-list">
            {form.rows.map((row, index) => (
              <div key={index} className="tracker-item-row">
                <div className="tracker-item-field">
                  <input
                    type="text"
                    value={row.item}
                    onChange={e => handleRowChange(index, 'item', e.target.value)}
                    placeholder="e.g. Approach Speed"
                    className={`tracker-form-input ${formErrors[`row_${index}_item`] ? 'input-error' : ''}`}
                  />
                  {formErrors[`row_${index}_item`] && (
                    <span className="field-error">{formErrors[`row_${index}_item`]}</span>
                  )}
                </div>

                <div className="tracker-item-field">
                  <select
                    value={row.type}
                    onChange={e => handleRowChange(index, 'type', e.target.value)}
                    className={`tracker-form-input tracker-form-select ${formErrors[`row_${index}_type`] ? 'input-error' : ''}`}
                  >
                    {TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {formErrors[`row_${index}_type`] && (
                    <span className="field-error">{formErrors[`row_${index}_type`]}</span>
                  )}
                </div>

                <button
                  className="tracker-row-remove"
                  onClick={() => handleRemoveRow(index)}
                  disabled={form.rows.length === 1}
                  title="Remove row"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          {/* Add row */}
          <button className="tracker-add-row-btn" onClick={handleAddRow}>
            <PlusCircle size={17} />
            Add Item
          </button>

          {/* Actions */}
          <div className="tracker-form-actions">
            <button className="btn-secondary" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </button>
            <button
              className="btn-new"
              onClick={handleSubmit}
              disabled={isSaving}
              style={{ opacity: isSaving ? 0.65 : 1 }}
            >
              {isSaving ? 'Saving…' : editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* ── List ── */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading default templates…</p>
        </div>
      ) : templates.length === 0 && !showForm ? (
        <div className="empty-state">
          <Sparkles size={48} style={{ color: '#c4b5fd', marginBottom: '1rem' }} />
          <h3>No default templates yet</h3>
          <p>Click <strong>+ New Default Template</strong> to add the first one.</p>
        </div>
      ) : templates.length > 0 && (
        <>
          {categories.map(cat => {
            const group = templates.filter(t => (t.category || 'General') === cat);
            return (
              <div key={cat} style={{ marginBottom: '1.5rem' }}>
                {/* Category heading */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  marginBottom: '0.6rem', paddingBottom: '0.4rem',
                  borderBottom: '2px solid #ede9fe'
                }}>
                  <Tag size={14} color="#7c3aed" />
                  <span style={{
                    fontSize: '0.78rem', fontWeight: 700,
                    color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.06em'
                  }}>
                    {cat}
                  </span>
                  <span style={{
                    marginLeft: 'auto', fontSize: '0.75rem',
                    color: '#9ca3af', fontWeight: 500
                  }}>
                    {group.length} template{group.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="table-container" style={{ marginBottom: 0 }}>
                  <table className="user-table">
                    <thead>
                      <tr>
                        <th>Heading</th>
                        <th>Items</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.map(tpl => (
                        <tr key={tpl.defaultTemplateId}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                              <Sparkles size={14} color="#7c3aed" />
                              <strong>{tpl.heading}</strong>
                            </div>
                          </td>
                          <td>
                            <div className="tracker-item-rows">
                              {(tpl.items || []).map((itm, i) => (
                                <div key={i} className="tracker-item-line">
                                  <span className="badge-item-name">{itm.item}</span>
                                  <span
                                    className="badge-type-pill"
                                    style={{ backgroundColor: TYPE_COLOURS[itm.type] || '#94a3b8' }}
                                  >
                                    {itm.type}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td>{formatDate(tpl.createdAt)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="action-btn edit"
                                title="Edit"
                                onClick={() => handleEdit(tpl)}
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                className="action-btn delete"
                                title="Delete"
                                onClick={() => handleDelete(tpl.defaultTemplateId)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          <div className="user-count" style={{ marginTop: '0.75rem' }}>
            {templates.length} default template{templates.length !== 1 ? 's' : ''}
          </div>
        </>
      )}
    </>
  );
};

export default AdminDefaultTemplates;
